// The site's logo lockup (2026-09-24): the head-and-gear mark in a tile, then
// the name stacked the way the full logo sets it — FACESHOT in steel, CHOP
// SHOP in copper underneath. The navbar and the footer both use it, so the
// two can't drift apart. The mark is /brand/mark-128.webp (4 KB); the same
// art at full size is the favicon and the app icons.
//
// Sizes (2026-09-24): `nav` steps down below 360px so the bar still fits the
// language toggle and a full 44px menu button on a 320px iPhone SE; `footer`
// is the larger signature lockup. The wordmark is Syne (tailwind
// fontFamily.display), the same face as the headings, at 700: Syne's 800 is
// a much wider cut (half as wide again) and would not fit a phone bar. CHOP
// SHOP is tracked out to the width of FACESHOT at every step, as in the logo,
// and never drops under the 11px type floor: on a sub-360px phone it keeps
// 11px and gives up tracking instead (2026-09-24; it was 9.5px there).

type BrandSize = 'nav' | 'footer';

const SIZES: Record<BrandSize, { tile: string; img: number; name: string; sub: string; gap: string }> = {
  nav: {
    tile: 'h-8 w-8 rounded-[10px] min-[360px]:h-9 min-[360px]:w-9 min-[360px]:rounded-xl lg:h-10 lg:w-10',
    img: 40,
    name: 'text-[13px] tracking-[0.03em] min-[360px]:text-[14.5px] min-[360px]:tracking-[0.05em] sm:text-[16px] lg:text-[17px]',
    sub: 'mt-[3px] text-[11px] tracking-[0.11em] min-[360px]:text-[11px] min-[360px]:tracking-[0.2em] sm:text-[11.5px] sm:tracking-[0.25em] lg:text-[12px] lg:tracking-[0.26em]',
    gap: 'gap-[7px] min-[360px]:gap-2.5',
  },
  footer: {
    tile: 'h-11 w-11 rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl',
    img: 48,
    name: 'text-[18px] tracking-[0.05em] sm:text-[20px]',
    sub: 'mt-1 text-[11px] tracking-[0.34em] sm:text-[12px]',
    gap: 'gap-3',
  },
};

export function BrandMark({
  alt,
  className = '',
  size = 'nav',
}: {
  alt: string;
  className?: string;
  size?: BrandSize;
}) {
  const s = SIZES[size];
  return (
    <span className={`flex min-w-0 items-center ${s.gap} ${className}`}>
      <span
        className={`relative flex shrink-0 items-center justify-center overflow-hidden border border-white/10 shadow-[0_12px_26px_rgba(0,0,0,0.45),0_0_22px_-6px_rgba(56,189,248,0.45)] transition-[transform,box-shadow] duration-500 ease-expo-out group-hover:-translate-y-0.5 group-hover:shadow-[0_16px_36px_rgba(0,0,0,0.55),0_0_28px_-4px_rgba(56,189,248,0.6)] ${s.tile}`}
      >
        <img src="/brand/mark-128.webp" alt={alt} width={s.img} height={s.img} className="h-full w-full scale-[1.14] object-cover" />
      </span>
      <span className="flex min-w-0 flex-col leading-none" aria-hidden="true">
        <span
          className={`whitespace-nowrap bg-[linear-gradient(180deg,#ffffff_0%,#dbe6f0_55%,#9fb3c6_100%)] bg-clip-text font-display font-bold uppercase text-transparent ${s.name}`}
        >
          FaceShot
        </span>
        <span className={`whitespace-nowrap font-display font-bold uppercase text-[#e8915f] ${s.sub}`}>
          Chop Shop
        </span>
      </span>
    </span>
  );
}
